import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "./databaseOverview.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBuilding,
    faStore,
    faBoxes,
    faTags,
    faMoneyBillWave,
    faChartLine,
    faTruckLoading,
    faClipboardList,
    faUsers,
    faMapMarkerAlt,
    faCalendarAlt,
    faInfoCircle,
    faUserShield,
} from "@fortawesome/free-solid-svg-icons";

const businessContext = {
    title: "Contexto del Negocio: Ferretería RetailDB",
    subtitle:
        "Visión general de la operación, sucursales y catálogo de productos disponibles para análisis.",
    sections: [
        {
            id: "branches",
            title: "Nuestras Sucursales",
            icon: faStore,
            description: "Operamos en 4 ubicaciones estratégicas, cada una con su propio inventario y gestión de costos.",
            color: "#7c4dff", // Purple for branches
            items: [
                { name: "Sucursal Córdoba", details: "Centro de distribución regional", icon: faMapMarkerAlt },
                { name: "Sucursal Mendoza", details: "Especializada en venta mayorista", icon: faMapMarkerAlt },
                { name: "Sucursal Palermo", details: "Tienda flagship y atención al público", icon: faMapMarkerAlt },
                { name: "Sucursal Rosario", details: "Logística y materiales de construcción", icon: faMapMarkerAlt },
            ],
        },
        {
            id: "catalog",
            title: "Catálogo de Productos",
            icon: faBoxes,
            description: "Ofrecemos una amplia gama de productos ferreteros organizados en categorías clave.",
            color: "#aa00ff", // Deep Purple Accent
            items: [
                { name: "Herramientas", details: "Manuales (martillos, destornilladores) y Eléctricas (taladros, sierras)", icon: faTags },
                { name: "Pinturas y Acabados", details: "Látex, esmaltes, rodillos y accesorios de pintura", icon: faTags },
                { name: "Fijaciones y Accesorios", details: "Tornillería, clavos, cintas métricas y seguridad industrial", icon: faTags },
                { name: "Materiales de Construcción", details: "Cementos, áridos y complementos de obra", icon: faTags },
            ],
        },
        {
            id: "business_data",
            title: "Información Disponible",
            icon: faChartLine,
            description: "Datos operativos y financieros disponibles para consulta y análisis exhaustivo.",
            color: "#651fff", // Deep Purple
            items: [
                { name: "Costos Unitarios", details: "Seguimiento histórico del costo por producto y sucursal", icon: faMoneyBillWave },
                { name: "Movimientos de Inventario", details: "Entradas, salidas y transferencias entre sucursales", icon: faTruckLoading },
                { name: "Ajustes de Inventario", details: "Mermas, devoluciones, promociones y correcciones", icon: faClipboardList },
                { name: "Ventas y Margen", details: "Volumen de ventas e impacto en la rentabilidad", icon: faChartLine },
            ],
        },
        {
            id: "team",
            title: "Equipo y Usuarios",
            icon: faUsers,
            description: "Gestión de acceso y roles dentro de la plataforma de análisis.",
            color: "#4a148c", // Dark Purple
            items: [
                { name: "Administradores", details: "Gestión total del sistema y configuración", icon: faUserShield },
                { name: "Analistas", details: "Acceso a reportes y consultas detalladas", icon: faUsers },
                { name: "Gerentes de Sucursal", details: "Visión específica de su unidad de negocio", icon: faStore },
            ],
        },
    ],
};


export default function DatabaseOverviewModal() {
    const [isOpen, setIsOpen] = useState(false);
    const [expandedSection, setExpandedSection] = useState(null);
    const dialogRef = useRef(null);
    const openBtnRef = useRef(null);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") setIsOpen(false);
        };
        if (isOpen) document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            dialogRef.current && dialogRef.current.focus();
        } else {
            openBtnRef.current && openBtnRef.current.focus();
        }
    }, [isOpen]);

    const toggleSection = (sectionId) => {
        setExpandedSection(expandedSection === sectionId ? null : sectionId);
    };

    return (
        <>
            {/* Botón para abrir modal */}
            <button
                ref={openBtnRef}
                onClick={() => setIsOpen(true)}
                className="db-overview-btn"
                title="Ver contexto del negocio"
            >
                <FontAwesomeIcon icon={faBuilding} /> {/* Changed icon to building */}
            </button>

            {/* Modal */}
            {isOpen && ReactDOM.createPortal(
                <div
                    className="db-modal-overlay"
                    onClick={() => setIsOpen(false)}
                    aria-hidden="true"
                    style={{ zIndex: 10000 }} // FIX: Ensure modal is on top of everything
                >
                    <div
                        className="db-modal-content"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="db-modal-title"
                        onClick={(e) => e.stopPropagation()}
                        tabIndex={-1}
                        ref={dialogRef}
                    >
                        {/* Cerrar */}
                        <button
                            onClick={() => setIsOpen(false)}
                            className="db-modal-close"
                            aria-label="Cerrar modal"
                            title="Cerrar"
                        >
                            ✕
                        </button>

                        <header className="db-modal-header" style={{ background: "linear-gradient(135deg, #250e48 0%, #5e35b1 100%)" }}> {/* Purple Header */}
                            <div className="db-header-icon" style={{ background: "rgba(255,255,255,0.15)" }}>
                                <FontAwesomeIcon icon={faBuilding} />
                            </div>
                            <div className="db-header-text">
                                <h2 id="db-modal-title">{businessContext.title}</h2>
                                <p className="db-modal-subtitle">{businessContext.subtitle}</p>
                            </div>
                        </header>

                        {/* Stats Summary */}
                        <div className="db-stats-bar" style={{ background: "rgba(124, 77, 255, 0.05)" }}>
                            <div className="db-stat">
                                <span className="db-stat-value" style={{ color: "#7c4dff" }}>4</span>
                                <span className="db-stat-label">Sucursales</span>
                            </div>
                            <div className="db-stat">
                                <span className="db-stat-value" style={{ color: "#7c4dff" }}>~500</span>
                                <span className="db-stat-label">Productos</span>
                            </div>
                            <div className="db-stat">
                                <span className="db-stat-value" style={{ color: "#7c4dff" }}>2025</span>
                                <span className="db-stat-label">Periodo Actual</span>
                            </div>
                        </div>

                        <div className="db-modal-scroll">

                            {/* Sections Grid */}
                            <div className="db-tables-grid">
                                {businessContext.sections.map((section) => (
                                    <div
                                        key={section.id}
                                        className={`db-table-card ${expandedSection === section.id ? "expanded" : ""}`}
                                        style={{ "--table-color": section.color }}
                                    >
                                        <button
                                            className="db-table-header"
                                            onClick={() => toggleSection(section.id)}
                                        >
                                            <div className="db-table-icon">
                                                <FontAwesomeIcon icon={section.icon} />
                                            </div>
                                            <div className="db-table-info">
                                                <h3>{section.title}</h3>
                                                <span className="db-table-count">{section.items.length} elementos clave</span>
                                            </div>
                                            <span className="db-table-toggle">
                                                {expandedSection === section.id ? "−" : "+"}
                                            </span>
                                        </button>

                                        <p className="db-table-desc">{section.description}</p>

                                        {expandedSection === section.id && (
                                            <div className="db-columns-list">
                                                {section.items.map((item, idx) => (
                                                    <div key={idx} className="db-column-item">
                                                        <div className="db-column-icon" style={{ color: section.color, background: `${section.color}1a` }}>
                                                            <FontAwesomeIcon icon={item.icon} />
                                                        </div>
                                                        <div className="db-column-details">
                                                            <div className="db-column-header">
                                                                <span className="db-column-name" style={{ background: `${section.color}1a`, color: "#333" }}>{item.name}</span>
                                                            </div>
                                                            <p className="db-column-desc">{item.details}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Business Insights Tips */}
                            <div className="db-tips-section" style={{ borderLeft: "4px solid #7c4dff" }}> {/* Purple Border */}
                                <h4 style={{ color: "#5e35b1" }}>
                                    <FontAwesomeIcon icon={faInfoCircle} style={{ color: "#7c4dff" }} />
                                    Tips para Análisis de Negocio
                                </h4>
                                <ul className="db-tips-list">
                                    <li style={{ borderLeftColor: "#b388ff" }}>
                                        <strong>Comparativas Regionales:</strong> Analiza el rendimiento de <strong>Córdoba vs Rosario</strong> para identificar oportunidades logísticas.
                                    </li>
                                    <li style={{ borderLeftColor: "#b388ff" }}>
                                        <strong>Análisis de Margen:</strong> Revisa el impacto de los <strong>Ajustes de Inventario</strong> en la rentabilidad final de productos clave.
                                    </li>
                                    <li style={{ borderLeftColor: "#b388ff" }}>
                                        <strong>Tendencias Temporales:</strong> Utiliza los datos de <strong>2024-2025</strong> para proyectar demandas estacionales en pinturas e impermeabilizantes.
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}
